'use client'

import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Loader2, ShieldCheck } from 'lucide-react'

type Props = {
  slug: string
  partySize: number
  /** ISO datetime de la reserva (mismo valor que se manda a POST /api/reservations). */
  scheduledAt: string
  accentColor?: string
  /** Se llama tras tokenizar la tarjeta. Recibe el INTENT token (nunca un BRIC). */
  onCaptured: (intentToken: string) => void
  onError?: (message: string) => void
}

/**
 * Cobro del DEPÓSITO de reserva (SALE > $0) del cliente.
 *
 * Monta el HOSTED Embedded Checkout (checkout.js) sobre la sesión SALE creada
 * server-side (POST /api/reservations/deposit-session → { intentToken, epxToken,
 * amountCents }). El iframe muestra el botón "Pay $X" real (EPX SÍ procesa > $0).
 * Diferencias propias del cliente:
 *   1. Muestra el MONTO + un consentimiento financiero ANTES de pedir la tarjeta;
 *      el iframe NO se monta hasta marcar el checkbox.
 *   2. Al completar el pago, entrega el INTENT token (nunca un BRIC). El servidor
 *      CONFIRMA el cargo (poll) y resuelve el BRIC en POST /api/reservations.
 */
export function ReservationDepositCapture({
  slug,
  partySize,
  scheduledAt,
  accentColor,
  onCaptured,
  onError,
}: Props) {
  const t = useTranslations('public.microsite')
  const accent = accentColor ?? '#ea580c'

  const [loadingSession, setLoadingSession] = useState(true)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [consent, setConsent] = useState(false)
  const [mounting, setMounting] = useState(false)
  const [mountError, setMountError] = useState<string | null>(null)

  // Datos de la sesión (no van a state que dispare el efecto de mount salvo epxToken,
  // que es el gatillo). El intentToken se entrega tal cual al completar.
  const [epxToken, setEpxToken] = useState<string | null>(null)
  const [amountCents, setAmountCents] = useState<number | null>(null)
  const intentTokenRef = useRef<string | null>(null)
  const onCapturedRef = useRef(onCaptured)
  const onErrorRef = useRef(onError)
  onCapturedRef.current = onCaptured
  onErrorRef.current = onError

  // 1) Pedir la sesión al montar: monto + tokens (sin montar el iframe todavía).
  useEffect(() => {
    let cancelled = false
    setLoadingSession(true)
    setSessionError(null)

    async function loadSession() {
      try {
        const res = await fetch('/api/reservations/deposit-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug, party_size: partySize, scheduled_at: scheduledAt }),
        })
        const data = (await res.json().catch(() => ({}))) as {
          intentToken?: string
          epxToken?: string
          amountCents?: number
          error?: string
        }
        if (!res.ok || !data.intentToken || !data.epxToken || typeof data.amountCents !== 'number') {
          throw new Error(data.error ?? 'session_failed')
        }
        if (cancelled) return
        intentTokenRef.current = data.intentToken
        setAmountCents(data.amountCents)
        setEpxToken(data.epxToken)
      } catch {
        if (!cancelled) {
          const msg = t('depositError')
          setSessionError(msg)
          onErrorRef.current?.(msg)
        }
      } finally {
        if (!cancelled) setLoadingSession(false)
      }
    }

    void loadSession()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, partySize, scheduledAt])

  // 2) Montar checkout.js SOLO tras el consentimiento (y con sesión lista).
  useEffect(() => {
    if (!consent || !epxToken) return

    let cancelled = false
    let scriptEl: HTMLScriptElement | null = null
    let unsubscribe: (() => void) | undefined
    setMounting(true)
    setMountError(null)

    scriptEl = document.createElement('script')
    scriptEl.src = 'https://checkout.north.com/checkout.js'
    scriptEl.async = true
    scriptEl.onload = async () => {
      if (cancelled) return
      let retries = 0
      while (!window.checkout && retries < 10) {
        await new Promise((r) => setTimeout(r, 50))
        retries++
      }
      if (cancelled) return
      if (!window.checkout) {
        const msg = t('depositError')
        setMountError(msg)
        setMounting(false)
        onErrorRef.current?.(msg)
        return
      }
      try {
        await window.checkout.mount(epxToken, 'redy-deposit-form')
        if (cancelled) return
        unsubscribe = window.checkout.onPaymentComplete?.(() => {
          // NUNCA un BRIC: el servidor lo resuelve desde el intent + la sesión.
          const intent = intentTokenRef.current
          if (intent) onCapturedRef.current(intent)
        })
      } catch {
        if (!cancelled) {
          const msg = t('depositError')
          setMountError(msg)
          onErrorRef.current?.(msg)
        }
      } finally {
        if (!cancelled) setMounting(false)
      }
    }
    scriptEl.onerror = () => {
      if (cancelled) return
      const msg = t('depositError')
      setMountError(msg)
      setMounting(false)
      onErrorRef.current?.(msg)
    }
    document.body.appendChild(scriptEl)

    return () => {
      cancelled = true
      unsubscribe?.()
      if (scriptEl?.parentNode) scriptEl.parentNode.removeChild(scriptEl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent, epxToken])

  const amount =
    amountCents != null
      ? (amountCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '—'

  if (loadingSession) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> {t('depositLoading')}
      </div>
    )
  }

  if (sessionError) {
    return <p className="text-sm text-red-500">{sessionError}</p>
  }

  return (
    <div className="space-y-4" style={{ color: '#171717' }}>
      {/* Monto del depósito + nota: se cobra ahora; devolución según la política del restaurante. */}
      <div className="rounded-2xl border border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600">
            <ShieldCheck className="w-4 h-4" style={{ color: accent }} />
            {t('depositAmountLabel')}
          </span>
          <span className="text-base font-bold">${amount}</span>
        </div>
        <p className="mt-1 text-xs text-gray-500">{t('depositSubtitle')}</p>
      </div>

      {/* Consentimiento financiero ANTES de pedir la tarjeta. */}
      <label className="flex items-start gap-2 text-xs text-gray-600 leading-snug">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5"
        />
        <span>{t('depositConsentLabel', { amount })}</span>
      </label>

      {/* Divulgación pasiva, mismo patrón visual que el consentimiento de
          PaymentStep. Antes no había NINGÚN enlace a política acá, y el depósito
          es un cobro real. */}
      <p className="text-[11px] leading-relaxed text-gray-500">
        {t.rich('depositLegal', {
          merchantRefund: (chunks) => (
            <a href={`/order/${slug}/refund-policy`} target="_blank" rel="noopener noreferrer" className="underline font-medium" style={{ color: accent }}>{chunks}</a>
          ),
          refund: (chunks) => (
            <a href="/refund" target="_blank" rel="noopener noreferrer" className="underline font-medium" style={{ color: accent }}>{chunks}</a>
          ),
        })}
      </p>

      {/* El iframe hosted se monta aquí SOLO tras marcar el consentimiento. */}
      {consent && (
        <div className="space-y-2">
          {mounting && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('depositCapturing')}
            </div>
          )}
          <div id="redy-deposit-form" className="w-full min-h-[440px]" />
          {mountError && <p className="text-sm text-red-500">{mountError}</p>}
        </div>
      )}
    </div>
  )
}
